import { initializeApp, getApp, getApps } from "firebase/app";
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";

function readEnv(key) {
  return String(process.env[key] || "").trim();
}

const firebaseConfig = {
  apiKey: readEnv("REACT_APP_FIREBASE_API_KEY"),
  authDomain: readEnv("REACT_APP_FIREBASE_AUTH_DOMAIN"),
  projectId: readEnv("REACT_APP_FIREBASE_PROJECT_ID"),
  storageBucket: readEnv("REACT_APP_FIREBASE_STORAGE_BUCKET"),
  messagingSenderId: readEnv("REACT_APP_FIREBASE_MESSAGING_SENDER_ID"),
  appId: readEnv("REACT_APP_FIREBASE_APP_ID"),
};

export const isFirebaseConfigured = Boolean(
  firebaseConfig.apiKey &&
    firebaseConfig.authDomain &&
    firebaseConfig.projectId &&
    firebaseConfig.appId
);

export const firebaseApp = isFirebaseConfigured
  ? getApps().length
    ? getApp()
    : initializeApp(firebaseConfig)
  : null;

export const firebaseAuth = firebaseApp ? getAuth(firebaseApp) : null;
export const firebaseDb = firebaseApp ? getFirestore(firebaseApp) : null;
export const firebaseStorage = firebaseApp ? getStorage(firebaseApp) : null;

let authReadyPromise = null;

export function isPermissionDeniedError(error) {
  const code = String(error?.code || "");
  const message = String(error?.message || "");
  return (
    code === "permission-denied" ||
    code === "storage/unauthorized" ||
    /missing or insufficient permissions/i.test(message) ||
    /permission[- ]denied/i.test(message)
  );
}

export function explainFirebaseError(error) {
  if (isPermissionDeniedError(error)) {
    return (
      "Firebase izin hatası: Yönetim yazmaları için users/{uid}.role = admin olan gerçek Firebase hesabıyla giriş yapın. " +
      "Firestore/Storage kuralları anonim yazmaya kapalıdır."
    );
  }
  if (error?.code === "auth/operation-not-allowed") {
    return "E-posta/şifre girişi kapalı. Firebase Console → Authentication → Sign-in method → Email/Password’ü etkinleştirin.";
  }
  if (error?.code === "auth/invalid-credential" || error?.code === "auth/wrong-password" || error?.code === "auth/user-not-found") {
    return "E-posta veya şifre hatalı.";
  }
  return error?.message || "Firebase hatası";
}

function waitForAuthReady() {
  if (!firebaseAuth) {
    return Promise.resolve(null);
  }
  if (firebaseAuth.currentUser) {
    return Promise.resolve(firebaseAuth.currentUser);
  }
  if (!authReadyPromise) {
    authReadyPromise = new Promise((resolve) => {
      const unsub = onAuthStateChanged(firebaseAuth, (user) => {
        unsub();
        authReadyPromise = null;
        resolve(user);
      });
    });
  }
  return authReadyPromise;
}

function isUsableAdminUser(user) {
  return Boolean(user && !user.isAnonymous);
}

/** Yönetim paneli yazmaları: anonim yok, oturum açmış kullanıcı zorunlu. */
export async function ensureFirebaseAuth() {
  if (!firebaseAuth) {
    throw new Error("Firebase yapılandırılmamış. web/.env dosyasını kontrol edin.");
  }
  const user = (await waitForAuthReady()) || firebaseAuth.currentUser;
  if (!isUsableAdminUser(user)) {
    throw new Error("Yönetim paneli için Firebase admin girişi gerekli.");
  }
  return user;
}

/** Public okuma için: varsa oturumu kullan, yoksa null (anonim açılmaz). */
export async function tryEnsureFirebaseAuth() {
  try {
    if (!firebaseAuth) return null;
    const user = (await waitForAuthReady()) || firebaseAuth.currentUser;
    return isUsableAdminUser(user) ? user : null;
  } catch {
    return null;
  }
}

export async function signInAdminWithEmail(email, password) {
  if (!firebaseAuth || !firebaseDb) {
    throw new Error("Firebase yapılandırılmamış. web/.env dosyasını kontrol edin.");
  }
  const credential = await signInWithEmailAndPassword(firebaseAuth, String(email).trim(), String(password));
  const uid = credential.user.uid;
  const profileSnap = await getDoc(doc(firebaseDb, "users", uid));
  const role = profileSnap.exists() ? String(profileSnap.data()?.role || "") : "";
  if (role !== "admin") {
    await signOut(firebaseAuth);
    const err = new Error("Bu hesap admin yetkisine sahip değil.");
    err.code = "auth/not-admin";
    throw err;
  }
  return {
    userId: uid,
    email: credential.user.email || email,
    displayName:
      String(profileSnap.data()?.displayName || "").trim() ||
      String(email).split("@")[0] ||
      "Admin",
    role: "admin",
  };
}

export async function signOutFirebase() {
  if (!firebaseAuth) return;
  await signOut(firebaseAuth);
}
